<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="mb-0">{{ $t("dmp.outputOptions.title") }}</h5>
            <div class="d-flex gap-1">
              <b-button
                v-if="!isTemplateMode"
                variant="primary"
                size="sm"
                @click="saveOutputOptions"
                :disabled="isSaving"
              >
                <b-spinner v-if="isSaving" small class="mr-25" />
                <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                {{ isSaving ? $t("actions.saving") : $t("actions.save") }}
              </b-button>
            </div>
          </div>

          <!-- Loading spinner -->
          <div v-if="isLoading" class="d-flex justify-content-center p-3">
            <b-spinner variant="primary" :label="$t('dmp.outputOptions.loadingOutputs')" />
            <span class="ml-2">{{ $t("dmp.outputOptions.loadingOutputs") }}</span>
          </div>

          <b-form v-else @submit.prevent="saveOutputOptions">
            <b-row>
              <!-- Left Column: Options -->
              <b-col cols="12" md="6">
                <b-card class="mb-1 border">
                  <h6 class="mb-2 font-weight-bold">
                    {{ $t("dmp.outputOptions.options") }}
                  </h6>

                  <b-row>
                    <b-col cols="12" sm="6">
                      <b-form-group :label="$t('dmp.outputOptions.cutoffOutputs')" label-for="cutoffOutputs">
                        <b-form-input id="cutoffOutputs" v-model="outputOptions.cutOffOutputs" type="text" maxlength="1"
                          @input="validateCutoffOutputs" @blur="validateCutoffOutputs(outputOptions.cutOffOutputs)" />
                        <b-form-text class="text-muted">
                          <small>{{ $t("dmp.outputOptions.cutoffOutputsRange") }}</small>
                        </b-form-text>
                      </b-form-group>
                    </b-col>

                    <b-col cols="12" sm="6">
                      <b-form-group label="Cutoff Time Minutes" label-for="cutoffTime">
                        <b-form-input id="cutoffTime" v-model="outputOptions.cutOffTime" type="text" maxlength="2"
                          @input="validateCutoffTime" @blur="validateCutoffTime(outputOptions.cutOffTime)" />
                        <b-form-text class="text-muted">
                          <small>{{ $t("dmp.outputOptions.cutoffTimeRange") }}</small>
                        </b-form-text>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <h6 class="mt-1 mb-2 font-weight-bold">
                    {{ $t("dmp.outputOptions.energySaving") }}
                  </h6>
                  <b-row>
                    <b-col cols="12" sm="6">
                      <b-form-group :label="$t('dmp.outputOptions.heatSaverTemp')" label-for="heatSaverTemp">
                        <b-form-input id="heatSaverTemp" v-model="outputOptions.heatSaverTemperatureOutput" type="text"
                          maxlength="2" @input="
                            validateTemperature($event, 'heatSaverTemperatureOutput')
                            " @blur="
                            validateTemperature(
                              outputOptions.heatSaverTemperatureOutput,
                              'heatSaverTemperatureOutput'
                            )
                            " />
                        <b-form-text class="text-muted">
                          <small>{{ $t("dmp.outputOptions.temperatureRange") }}</small>
                        </b-form-text>
                      </b-form-group>
                    </b-col>

                    <b-col cols="12" sm="6">
                      <b-form-group :label="$t('dmp.outputOptions.coolSaverTemp')" label-for="coolSaverTemp">
                        <b-form-input id="coolSaverTemp" v-model="outputOptions.coolSaverTemperatureOutput" type="text"
                          maxlength="2" @input="
                            validateTemperature($event, 'coolSaverTemperatureOutput')
                            " @blur="
                            validateTemperature(
                              outputOptions.coolSaverTemperatureOutput,
                              'coolSaverTemperatureOutput'
                            )
                            " />
                        <b-form-text class="text-muted">
                          <small>{{ $t("dmp.outputOptions.temperatureRange") }}</small>
                        </b-form-text>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>

              <!-- Right Column: Outputs -->
              <b-col cols="12" md="6">
                <b-card class="mb-2 border">
                  <h6 class="mb-2 font-weight-bold">
                    {{ $t("dmp.outputOptions.outputs") }}
                  </h6>

                  <b-row>
                    <b-col v-for="(output, index) in outputsList" :key="output.key" cols="12" sm="6">
                      <b-form-group :label="output.label" :label-for="output.key" class="mb-2">
                        <div class="d-flex gap-2">
                          <b-form-select v-model="outputPrefixes[output.key]" :options="prefixOptions"
                            style="max-width: 60px" @change="handlePrefixChange(output.key)" />
                          <b-form-input :id="output.key" v-model="outputNumbers[output.key]" type="text"
                            :maxlength="outputPrefixes[output.key] ? 2 : 3"
                            @input="validateOutputWithPrefix(output.key, false)"
                            @blur="validateOutputWithPrefix(output.key, true)" />
                        </div>
                        <b-form-text v-if="outputErrors[output.key]" class="text-danger">
                          <small>{{ outputErrors[output.key] }}</small>
                        </b-form-text>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  inject,
  getCurrentInstance,
} from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import store from "@/store";
import { useTemplateMode } from "./useTemplateMode";

export default {
  name: "ConfigOutputOptions",
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    outputOptionsData: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const toast = useToast();
    const { t } = useI18nUtils();
    const { refFormObserver, getValidationState } = formValidation();
    const { isTemplateMode, emitTemplateUpdate } = useTemplateMode(props, emit);

    const isLoading = computed(() => props.loading);
    const isSaving = ref(false);

    const outputOptions = ref({
      // Options
      cutOffOutputs: "",
      cutOffTime: "",

      // Energy Saving
      heatSaverTemperatureOutput: "",
      coolSaverTemperatureOutput: "",

      // Outputs
      commFailOutput: "",
      fireAlarmOutput: "",
      fireTroubleOutput: "",
      panicAlarmOutput: "",
      ambushOutput: "",
      entryDelayOutput: "",
      beginExitOutput: "",
      endExitOutput: "",
      readyOutput: "",
      phoneTroubleOutput: "",
      lateToCloseOutput: "",
      deviceFailedOutput: "",
      sensorResetOutput: "",
      closingWaitOutput: "",
      disarmedOutput: "",
      armAlarmOutput: "",
      supervisoryAlarmOutput: "",
      armedHomePerimeterOutput: "",
      armedAllAwayOutput: "",
      armedSleepOutput: "",
      carbonMonoxideOutput: "",
      lockdownOutput: "",
      zoneMonitorOutput: "",
    });

    const outputPrefixes = ref({});
    const outputNumbers = ref({});
    const outputErrors = ref({});

    const prefixOptions = [
      { value: "", text: "" },
      { value: "D", text: "D" },
      { value: "F", text: "F" },
      { value: "G", text: "G" },
    ];

    const outputsList = computed(() => [
      { key: "commFailOutput", label: t("dmp.outputOptions.commFailOutput") },
      { key: "fireAlarmOutput", label: t("dmp.outputOptions.fireAlarmOutput") },
      { key: "fireTroubleOutput", label: t("dmp.outputOptions.fireTroubleOutput") },
      { key: "panicAlarmOutput", label: t("dmp.outputOptions.panicAlarmOutput") },
      { key: "ambushOutput", label: t("dmp.outputOptions.ambushOutput") },
      { key: "entryDelayOutput", label: "Entry Output" },
      { key: "beginExitOutput", label: t("dmp.outputOptions.beginExitOutput") },
      { key: "endExitOutput", label: t("dmp.outputOptions.endExitOutput") },
      { key: "readyOutput", label: t("dmp.outputOptions.readyOutput") },
      { key: "phoneTroubleOutput", label: t("dmp.outputOptions.phoneTroubleOutput") },
      { key: "lateToCloseOutput", label: t("dmp.outputOptions.lateToCloseOutput") },
      { key: "deviceFailedOutput", label: "Device Fail Output" },
      { key: "sensorResetOutput", label: t("dmp.outputOptions.sensorResetOutput") },
      { key: "closingWaitOutput", label: t("dmp.outputOptions.closingWaitOutput") },
      { key: "disarmedOutput", label: t("dmp.outputOptions.disarmedOutput") },
      { key: "armAlarmOutput", label: t("dmp.outputOptions.armAlarmOutput") },
      {
        key: "supervisoryAlarmOutput",
        label: t("dmp.outputOptions.supervisoryAlarmOutput"),
      },
      { key: "armedHomePerimeterOutput", label: t("dmp.outputOptions.armedHomePerimeterOutput") },
      { key: "armedAllAwayOutput", label: t("dmp.outputOptions.armedAllAwayOutput") },
      { key: "armedSleepOutput", label: t("dmp.outputOptions.armedSleepOutput") },
      { key: "carbonMonoxideOutput", label: t("dmp.outputOptions.carbonMonoxideOutput") },
      { key: "lockdownOutput", label: t("dmp.outputOptions.lockdownOutput") },
      { key: "zoneMonitorOutput", label: t("dmp.outputOptions.zoneMonitorOutput") },
    ]);

    const loadOutputOptions = () => {
      if (!props.outputOptionsData) {
        console.warn("Output options data not provided");
        return;
      }

      try {
        const data = { ...props.outputOptionsData };

        if (data.heatSaverTemperatureOutput === "00") {
          data.heatSaverTemperatureOutput = "";
        }
        if (data.coolSaverTemperatureOutput === "00") {
          data.coolSaverTemperatureOutput = "";
        }
        if (data.cutOffOutputs === "0") {
          data.cutOffOutputs = "";
        }
        if (data.cutOffTime === "00") {
          data.cutOffTime = "";
        }

        outputOptions.value = {
          ...outputOptions.value,
          ...data,
        };

        const prefixes = {};
        const numbers = {};
        const errors = {};

        // Parse output values into prefix and number
        outputsList.value.forEach((output) => {
          const value = outputOptions.value[output.key] || "";
          const match = value.match(/^([DFG])?(\d+)$/);
          if (match) {
            const prefix = match[1] || "";
            const number = parseInt(match[2], 10);
            prefixes[output.key] = prefix;
            numbers[output.key] = number === 0 ? "" : String(number);
          } else {
            prefixes[output.key] = "";
            numbers[output.key] = "";
          }
          errors[output.key] = "";
        });

        outputPrefixes.value = prefixes;
        outputNumbers.value = numbers;
        outputErrors.value = errors;
      } catch (error) {
        console.error("Error loading output options:", error);
      }
    };

    // Construye la sección en forma GET para el template (combina prefijo +
    // número de cada salida, igual que el guardado normal).
    const buildTemplateItem = () => {
      const dataToSave = { ...outputOptions.value };
      outputsList.value.forEach((output) => {
        const prefix = outputPrefixes.value[output.key] || "";
        let number = outputNumbers.value[output.key] || "";
        const padding = prefix ? 2 : 3;
        number = number.padStart(padding, "0");
        dataToSave[output.key] = prefix + number;
      });
      return { ...(props.outputOptionsData || {}), ...dataToSave };
    };

    // Misma validación que saveOutputOptions: no debe haber errores por salida.
    const validateTemplateSection = async () =>
      !outputsList.value.some((output) => outputErrors.value[output.key]);

    const saveOutputOptions = async () => {
      let hasErrors = false;
      outputsList.value.forEach((output) => {
        if (outputErrors.value[output.key]) {
          hasErrors = true;
        }
      });

      if (hasErrors) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: "Please fix all validation errors before saving",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }

      isSaving.value = true;
      store.commit("dmp_panels/setOperationActive", true);
      try {
        // Combine prefix and number for each output
        const dataToSave = { ...outputOptions.value };
        outputsList.value.forEach((output) => {
          const prefix = outputPrefixes.value[output.key] || "";
          let number = outputNumbers.value[output.key] || "";
          // If has prefix (D, F, G), use 2 digits, else 3 digits
          const padding = prefix ? 2 : 3;
          number = number.padStart(padding, "0");
          dataToSave[output.key] = prefix + number;
        });

        if (isTemplateMode.value) {
          const item = { ...(props.outputOptionsData || {}), ...dataToSave };
          emitTemplateUpdate("output_options", item);
          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.outputOptions.messages.saveSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
          store.commit("dmp_panels/setOperationActive", false);
          isSaving.value = false;
          return;
        }

        const response = await store.dispatch("dmp_panels/setOutputOption", {
          uuid: props.uuid,
          options: dataToSave,
        });

        if (response.success) {
          emit("operation-completed");

          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.outputOptions.messages.saveSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
        } else {
          throw new Error(response.message || "Failed to save output options");
        }
      } catch (error) {
        console.error("Error saving output options:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.outputOptions.messages.saveError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        store.commit("dmp_panels/setOperationActive", false);
        isSaving.value = false;
      }
    };


    const validateCutoffOutputs = (value) => {
      const cleanValue = (value || "").replace(/[^0-9]/g, "");
      const numValue = parseInt(cleanValue, 10);

      if (!cleanValue || numValue === 0 || (numValue >= 1 && numValue <= 6)) {
        outputOptions.value.cutOffOutputs = cleanValue && numValue !== 0 ? cleanValue : "";
      } else {
        outputOptions.value.cutOffOutputs = "";
      }
    };

    const validateCutoffTime = (value) => {
      const cleanValue = (value || "").replace(/[^0-9]/g, "");
      const numValue = parseInt(cleanValue, 10);

      if (!cleanValue || numValue === 0 || (numValue >= 55 && numValue <= 95)) {
        outputOptions.value.cutOffTime = cleanValue && numValue !== 0 ? cleanValue : "";
      } else {
        outputOptions.value.cutOffTime = "";
      }
    };

    const validateTemperature = (value, field) => {
      const cleanValue = (value || "").replace(/[^0-9]/g, "");
      const numValue = parseInt(cleanValue, 10);

      if (!cleanValue || numValue === 0 || (numValue >= 55 && numValue <= 95)) {
        outputOptions.value[field] = cleanValue && numValue !== 0 ? cleanValue : "";
      } else {
        outputOptions.value[field] = "";
      }
    };

    const validateOutputWithPrefix = (field, isBlur = false) => {
      const prefix = outputPrefixes.value[field] || "";
      let number = outputNumbers.value[field] || "";

      number = number.replace(/[^0-9]/g, "");
      outputNumbers.value[field] = number;

      const padding = prefix ? 2 : 3;
      if (!number || parseInt(number) === 0) {
        outputNumbers.value[field] = "";
        outputErrors.value[field] = "";
        outputOptions.value[field] = "".padStart(padding, "0"); 
        return;
      }

      const numValue = parseInt(number);

      let isValid = false;
      let errorMessage = "";

      if (prefix === "D") {
        isValid = numValue >= 1 && numValue <= 16;
        errorMessage = isValid ? "" : "Valid range for D: 1-16";
      } else if (prefix === "F" || prefix === "G") {
        isValid = numValue >= 1 && numValue <= 20;
        errorMessage = isValid ? "" : `Valid range for ${prefix}: 1-20`;
      } else {
        isValid =
          (numValue >= 1 && numValue <= 6) ||
          (numValue >= 450 && numValue <= 474) ||
          (numValue >= 480 && numValue <= 999);
        errorMessage = isValid ? "" : "Valid ranges: 1-6, 450-474, 480-999";
      }

      outputErrors.value[field] = errorMessage;

      if (isValid) {
        outputNumbers.value[field] = number; 
        outputOptions.value[field] = prefix + number.padStart(padding, "0"); 
      } else {
        outputOptions.value[field] = "";
      }
    };

    const handlePrefixChange = (field) => {
      const newPrefix = outputPrefixes.value[field] || "";

      if (newPrefix) {
        outputNumbers.value[field] = "1";
        outputOptions.value[field] = newPrefix + "1";
      } else {
        outputNumbers.value[field] = "";
        outputOptions.value[field] = "";
      }

      outputErrors.value[field] = "";
    };

    watch(
      () => props.outputOptionsData,
      (newData) => {
        if (newData) {
          loadOutputOptions();
        }
      },
      { deep: true }
    );

    const registerTemplateSection = inject("registerTemplateSection", null);
    const unregisterTemplateSection = inject("unregisterTemplateSection", null);

    onMounted(() => {
      loadOutputOptions();
      if (isTemplateMode.value && registerTemplateSection) {
        registerTemplateSection("output_options", {
          validate: validateTemplateSection,
          collect: buildTemplateItem,
        });
      }
    });

    onBeforeUnmount(() => {
      if (isTemplateMode.value && unregisterTemplateSection) {
        unregisterTemplateSection("output_options");
      }
    });

    return {
      isLoading,
      isSaving,
      outputOptions,
      outputsList,
      outputPrefixes,
      outputNumbers,
      outputErrors,
      prefixOptions,
      saveOutputOptions,
      validateCutoffOutputs,
      validateCutoffTime,
      validateTemperature,
      validateOutputWithPrefix,
      handlePrefixChange,
      refFormObserver,
      getValidationState,
      isTemplateMode,
    };
  },
};
</script>

<style scoped lang="scss">
.border {
  border: 1px solid #ebe9f1;
}

.gap-2 {
  gap: 0.5rem;
}

// Dark mode adjustments
.dark-layout {
  .border {
    border-color: #404656;
  }
}
</style>
